// The eleventh question: the PsycheMatrix placement that is not a film.
//
// After the tenth trivia question, the game asks one more, in its own screen:
// the first item of the PsycheMatrix assessment. There is no right answer;
// the reveal says so and hands the player to the assessment with the answer
// they just gave. Design source: the 2026-09-11 session; the older film
// interstitial (PmInterstitial) stays behind PM_AD.mode = 'film'.
//
// The items below are VERBATIM from PsycheMatrix
// src/assessmentData/professionalDynamicsV2.ts (ids, labels, descriptions).
// Four of the six scenario items rotate by day; the two left out (PD2, PD4)
// read as management strategy, the four kept read as a person deciding.
// Re-copy from the source if that file changes; never paraphrase.

import { dailyNumber } from '@wot/shared';

/** The archetype each option scores toward, kept for analytics only: the screen never reads one answer back to the player. */
export type PmArchetype = 'WARRIOR' | 'RULER' | 'MAGICIAN' | 'SAGE' | 'CAREGIVER' | 'INNOCENT' | 'CREATOR' | 'EXPLORER' | 'GUARDIAN' | 'JESTER';

export interface PmOption { id: string; label: string; description: string; archetype: PmArchetype }
export interface PmItem { id: string; text: string; options: PmOption[] }

export const PM_QUESTIONS: PmItem[] = [
  {
    id: 'PD1',
    text: 'Your team is falling behind on a complex project. What do you do first?',
    options: [
      { id: 'PD1_A', archetype: 'WARRIOR', label: 'Act fast and reset execution', description: 'Clarify priorities, remove blockers, and drive immediate action.' },
      { id: 'PD1_B', archetype: 'RULER', label: 'Re-align ownership and decision rights', description: 'Set direction, assign owners, and create a decisive plan.' },
      { id: 'PD1_C', archetype: 'SAGE', label: 'Diagnose root causes before changing plan', description: 'Run a quick analysis to understand why progress slipped.' },
      { id: 'PD1_D', archetype: 'MAGICIAN', label: 'Shift the approach with a new method', description: 'Introduce a smarter workflow or tool to change the trajectory.' },
      { id: 'PD1_E', archetype: 'CAREGIVER', label: 'Stabilize the team and remove stress', description: 'Check capacity, support individuals, and protect morale.' },
    ],
  },
  {
    id: 'PD3',
    text: 'Two strong team members are in conflict and productivity is dropping. You:',
    options: [
      { id: 'PD3_A', archetype: 'CAREGIVER', label: 'Start with empathy and private conversations', description: 'Understand emotions and needs before solving the conflict.' },
      { id: 'PD3_B', archetype: 'GUARDIAN', label: 'Set clear boundaries and expectations', description: 'Protect team stability with explicit rules and follow-up.' },
      { id: 'PD3_C', archetype: 'WARRIOR', label: 'Address it directly and move to action', description: 'Bring them together and push toward a concrete resolution.' },
      { id: 'PD3_D', archetype: 'SAGE', label: 'Clarify the underlying misunderstandings', description: 'Surface assumptions and align on shared facts.' },
      { id: 'PD3_E', archetype: 'JESTER', label: 'Lower the tension and reframe the situation', description: 'Use humor and perspective to reset the emotional tone.' },
    ],
  },
  {
    id: 'PD5',
    text: 'You must persuade skeptical stakeholders to back your idea. You rely most on:',
    options: [
      { id: 'PD5_A', archetype: 'SAGE', label: 'Clear reasoning and evidence', description: 'Make the case with data, logic, and careful framing.' },
      { id: 'PD5_B', archetype: 'RULER', label: 'Authority and strategic alignment', description: 'Anchor the ask to organizational priorities and ownership.' },
      { id: 'PD5_C', archetype: 'MAGICIAN', label: 'A reframing that changes how they see the problem', description: 'Shift the narrative so the decision feels obvious.' },
      { id: 'PD5_D', archetype: 'INNOCENT', label: 'Honesty, clarity, and trust', description: 'Keep it simple, sincere, and transparent.' },
      { id: 'PD5_E', archetype: 'JESTER', label: 'Energy, storytelling, and memorable moments', description: 'Bring people with you through emotion and engagement.' },
    ],
  },
  {
    id: 'PD6',
    text: 'A solution needs to be created under tight constraints. You naturally:',
    options: [
      { id: 'PD6_A', archetype: 'CREATOR', label: 'Prototype and refine until it works', description: 'Build something tangible and improve through iteration.' },
      { id: 'PD6_B', archetype: 'MAGICIAN', label: 'Look for an unconventional shortcut', description: 'Recombine ideas to unlock a new path.' },
      { id: 'PD6_C', archetype: 'GUARDIAN', label: 'Choose a safe plan and execute reliably', description: 'Prioritize predictability and risk management.' },
      { id: 'PD6_D', archetype: 'WARRIOR', label: 'Push through with focus and urgency', description: 'Drive execution and make fast trade-offs.' },
      { id: 'PD6_E', archetype: 'EXPLORER', label: 'Try multiple approaches quickly', description: 'Experiment and see what sticks.' },
    ],
  },
];

export const PM_QUESTION = {
  creative: 'q11_v1',
  format: 'question',
  /** the assessment's own route; a1 carries the answer given here (see the PsycheMatrix handoff) */
  ctaBase: 'https://psychematrix.io/assessment',
  utm: 'utm_source=wot&utm_medium=question&utm_campaign=q11_v1',
} as const;

/** One item a day, same for every player that day, so the rotation is honest and predictable. */
export function pmQuestionForToday(): PmItem {
  const i = ((dailyNumber() % PM_QUESTIONS.length) + PM_QUESTIONS.length) % PM_QUESTIONS.length;
  return PM_QUESTIONS[i]!;
}

/** The assessment link carrying the answer just given, or none when the player skipped. */
export function pmCtaUrl(answer?: { question: string; option: string }): string {
  // the option id carries its item (PD1_C is item PD1), so one value is enough
  const a1 = answer ? `&a1=${encodeURIComponent(answer.option)}` : '';
  return `${PM_QUESTION.ctaBase}?${PM_QUESTION.utm}${a1}`;
}
